import React, { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Table, Button, Modal, Form, Pagination } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import { BsTrash, BsImage, BsCalendarEvent } from "react-icons/bs";
import { FaEdit } from "react-icons/fa";
import Cropper from "react-easy-crop";
import { Area } from "react-easy-crop/types";
import AsyncSelect from "react-select/async";
import type { GroupBase, StylesConfig } from "react-select";
import { useFindEvents } from "../../api/queries/useFindEvents";
import { useSaveEvent } from "../../api/mutations/useSaveEvent";
import { useGetMemberships } from "../../api/queries/useGetMemberships";
import { Event } from "../../types/Types";
import { useDeleteEvent } from "../../api/mutations/useDeleteEvent";
import { formatCurrencyAmount } from "../../utils/formatCurrency";
import { useTranslation } from "react-i18next";
import {
  formatDateForInput,
  formatTodayForDateInput,
  formatYesterdayForDateInput,
} from "../../utils/formatDateForInput";
import {
  defaultSelectedMembershipIds,
  isEventOpenToAllMemberships,
  serializeAllowedMembershipIds,
} from "../../utils/eventMembershipVisibility";
import { getCroppedImg } from "../../utils/getCroppedImg";
import {
  CityOption,
  loadCityOptions,
} from "../../utils/cityAsyncSelectOptions";
import AdminPanelHeader from "./AdminPanelHeader";

const BANNER_ASPECT = 4 / 3;
const BANNER_FILENAME = "banner.jpg";
const BANNER_MIN_ZOOM = 0.5;
const BANNER_MAX_ZOOM = 3;
const CROPPER_CONTAINER_STYLE = {
  position: "relative" as const,
  width: "100%",
  flex: "1 1 auto",
  minHeight: 120,
  background: "#333",
};

type CitySelectStyles = StylesConfig<CityOption, false, GroupBase<CityOption>>;

const CITY_SELECT_STYLES: CitySelectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? "#86b7fe" : base.borderColor || "#ced4da",
    boxShadow: state.isFocused
      ? "0 0 0 0.25rem rgba(13, 110, 253, 0.25)"
      : base.boxShadow || "none",
    "&:hover": {
      borderColor: state.isFocused ? "#86b7fe" : base.borderColor || "#ced4da",
    },
    minHeight: 38,
  }),
  menu: (base) => ({ ...base, zIndex: 9999 }),
  menuPortal: (base) => ({ ...base, zIndex: 10050 }),
};

// const EVENT_CURRENCY_OPTIONS = [
//   { value: "gbp", label: "GBP (£)" },
//   { value: "usd", label: "USD ($)" },
//   { value: "eur", label: "EUR (€)" },
//   { value: "aed", label: "AED" },
//   { value: "sar", label: "SAR" },
//   { value: "cad", label: "CAD" },
//   { value: "aud", label: "AUD" },
//   { value: "chf", label: "CHF" },
//   { value: "jpy", label: "JPY (¥)" },
// ] as const;

const ManageEvents: React.FC = () => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedMembershipIds, setSelectedMembershipIds] = useState<number[]>(
    [],
  );
  const [membershipSelectionError, setMembershipSelectionError] = useState("");
  const [visibleToAll, setVisibleToAll] = useState(true);
  const [earlyBirdEnabled, setEarlyBirdEnabled] = useState(false);
  const [eventDateInput, setEventDateInput] = useState("");
  const [earlyBirdEndDateInput, setEarlyBirdEndDateInput] = useState("");
  const [memberEventDiscountEligible, setMemberEventDiscountEligible] =
    useState(true);
  const [location, setLocation] = useState("");
  const [locationError, setLocationError] = useState("");
  const [bannerPreview, setBannerPreview] = useState<string | null>(null);
  const [bannerSource, setBannerSource] = useState<string | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const membershipFormInitializedRef = useRef<string | null>(null);
  const bannerInputRef = useRef<HTMLInputElement | null>(null);
  const bannerPointerRef = useRef<{ x: number; y: number; dragged: boolean } | null>(
    null,
  );
  const pageSize = 10;

  const navigate = useNavigate();
  const { data: memberships = [] } = useGetMemberships({ enabled: modalOpen });
  const allMembershipIds = useMemo(
    () => memberships.map((m) => m.id),
    [memberships],
  );

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm]);

  // Debounce search term
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearchTerm(searchQuery);
    }, 500);

    return () => {
      clearTimeout(handler);
    };
  }, [searchQuery]);

  const { data: events, refetch } = useFindEvents({
    pageSize,
    page: currentPage,
    searchTerm: debouncedSearchTerm || undefined,
  });

  const resetBannerState = useCallback((restoreExisting = false) => {
    setBannerSource(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    if (bannerInputRef.current) bannerInputRef.current.value = "";
    setBannerPreview(
      restoreExisting ? editingEvent?.bannerImage || null : null,
    );
  }, [editingEvent?.bannerImage]);

  const { mutate: updateEvent, isPending } = useSaveEvent({
    onSuccess: () => {
      setModalOpen(false);
      resetBannerState();
      refetch();
    },
  });

  const { mutate: deleteEvent, isPending: isDeleting } = useDeleteEvent({
    onSuccess: () => {
      setShowDeleteModal(false);
      setEventToDelete(null);
      refetch();
    },
  });

  const handleDelete = (event: Event) => {
    setEventToDelete(event);
    setShowDeleteModal(true);
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setMembershipSelectionError("");
    setLocation(event.location || "");
    setLocationError("");
    setBannerPreview(event.bannerImage || null);
    setBannerSource(null);
    setCrop({ x: 0, y: 0 });
    setZoom(1);
    setCroppedAreaPixels(null);
    setModalOpen(true);
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setMembershipSelectionError("");
    setLocation("");
    setLocationError("");
    resetBannerState();
    setModalOpen(true);
  };

  useEffect(() => {
    if (!modalOpen) {
      membershipFormInitializedRef.current = null;
      return;
    }

    const ebPrice = editingEvent?.earlyBirdPrice ?? 0;
    const ebEndFormatted = editingEvent?.earlyBirdDateEnd
      ? formatDateForInput(editingEvent.earlyBirdDateEnd)
      : "";
    const hasActiveEarlyBirdEnd =
      !!ebEndFormatted &&
      ebEndFormatted !== "1970-01-01" &&
      ebEndFormatted >= formatTodayForDateInput();

    setEarlyBirdEnabled(ebPrice > 0 || hasActiveEarlyBirdEnd);
    setMemberEventDiscountEligible(
      editingEvent?.memberEventDiscountEligible !== false,
    );
    setEventDateInput(
      editingEvent?.eventDate
        ? formatDateForInput(editingEvent.eventDate)
        : formatTodayForDateInput(),
    );
    setEarlyBirdEndDateInput(
      ebEndFormatted && ebEndFormatted !== "1970-01-01"
        ? ebEndFormatted
        : formatYesterdayForDateInput(),
    );
  }, [
    modalOpen,
    editingEvent?.id,
    editingEvent?.earlyBirdPrice,
    editingEvent?.earlyBirdDateEnd,
    editingEvent?.eventDate,
    editingEvent?.memberEventDiscountEligible,
  ]);

  useEffect(() => {
    if (!modalOpen || memberships.length === 0) return;

    const initKey =
      editingEvent?.id != null ? `edit-${editingEvent.id}` : "create";
    if (membershipFormInitializedRef.current === initKey) return;
    membershipFormInitializedRef.current = initKey;

    const openToAll = isEventOpenToAllMemberships(
      editingEvent?.allowedMembershipIds,
    );
    setVisibleToAll(openToAll);
    setSelectedMembershipIds(
      openToAll
        ? allMembershipIds
        : defaultSelectedMembershipIds(
            memberships,
            editingEvent?.allowedMembershipIds,
          ),
    );
    setMembershipSelectionError("");
  }, [
    modalOpen,
    editingEvent?.id,
    editingEvent?.allowedMembershipIds,
    memberships,
    allMembershipIds,
  ]);

  const handleVisibleToAllChange = (checked: boolean) => {
    setVisibleToAll(checked);
    if (checked) {
      setSelectedMembershipIds(allMembershipIds);
    }
    setMembershipSelectionError("");
  };

  const toggleMembershipSelection = (membershipId: number) => {
    if (visibleToAll) return;
    setSelectedMembershipIds((prev) =>
      prev.includes(membershipId)
        ? prev.filter((id) => id !== membershipId)
        : [...prev, membershipId],
    );
    setMembershipSelectionError("");
  };

  const openBannerFilePicker = useCallback(() => {
    if (bannerInputRef.current) {
      // Allow selecting the same file again to replace.
      bannerInputRef.current.value = "";
      bannerInputRef.current.click();
    }
  }, []);

  const onBannerCropPointerDown = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      bannerPointerRef.current = {
        x: e.clientX,
        y: e.clientY,
        dragged: false,
      };
    },
    [],
  );

  const onBannerCropPointerMove = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      const start = bannerPointerRef.current;
      if (!start || start.dragged) return;
      const dx = Math.abs(e.clientX - start.x);
      const dy = Math.abs(e.clientY - start.y);
      if (dx > 6 || dy > 6) {
        start.dragged = true;
      }
    },
    [],
  );

  const onBannerCropPointerUp = useCallback(() => {
    const start = bannerPointerRef.current;
    bannerPointerRef.current = null;
    // Click (no drag) → replace image; drag/pan and wheel zoom stay on Cropper.
    if (start && !start.dragged) {
      openBannerFilePicker();
    }
  }, [openBannerFilePicker]);

  const onBannerCropComplete = useCallback(
    (_croppedArea: Area, croppedPixels: Area) => {
      setCroppedAreaPixels(croppedPixels);
    },
    [],
  );

  const onBannerFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onloadend = () => {
        setBannerSource(reader.result as string);
        setBannerPreview(null);
        setCrop({ x: 0, y: 0 });
        setZoom(1);
        setCroppedAreaPixels(null);
      };
      reader.readAsDataURL(file);
    },
    [],
  );

  const handleSave = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    let allowedMembershipIds: string | null | undefined = null;
    if (!visibleToAll) {
      if (selectedMembershipIds.length === 0) {
        setMembershipSelectionError(
          "Select All or at least one membership type.",
        );
        return;
      }
      allowedMembershipIds = serializeAllowedMembershipIds(
        selectedMembershipIds,
        allMembershipIds,
      );
    }

    if (!location.trim()) {
      setLocationError(t("explorer.signup.cityRequired"));
      return;
    }
    setLocationError("");

    const form = e.currentTarget;
    const formData = new FormData();

    if (editingEvent?.id) {
      formData.append("id", String(editingEvent.id));
    }

    formData.append(
      "name",
      (form.elements.namedItem("name") as HTMLInputElement)?.value || "",
    );
    formData.append("description", form.description.value);
    formData.append("price", String(parseFloat(form.price.value)));
    formData.append(
      "earlyBirdPrice",
      String(
        earlyBirdEnabled
          ? parseFloat(
              (form.elements.namedItem("earlyBirdPrice") as HTMLInputElement)
                .value,
            )
          : 0,
      ),
    );
    formData.append("seats", String(parseInt(form.numberAvailable.value, 10)));
    formData.append(
      "earlyBirdDateEnd",
      earlyBirdEnabled ? earlyBirdEndDateInput : formatYesterdayForDateInput(),
    );
    formData.append("eventDate", eventDateInput);
    formData.append("location", location.trim());
    formData.append(
      "productId",
      (form.elements.namedItem("productId") as HTMLInputElement)?.value || "",
    );
    // Keep GBP fixed for now to avoid impacting current production currency behavior.
    formData.append("currency", "gbp");

    const emailSubject = (
      form.elements.namedItem(
        "bookingConfirmationEmailSubject",
      ) as HTMLInputElement
    )?.value?.trim();
    const emailBody = (
      form.elements.namedItem(
        "bookingConfirmationEmailBodyHtml",
      ) as HTMLTextAreaElement
    )?.value?.trim();
    if (emailSubject) {
      formData.append("bookingConfirmationEmailSubject", emailSubject);
    }
    if (emailBody) {
      formData.append("bookingConfirmationEmailBodyHtml", emailBody);
    }
    if (allowedMembershipIds) {
      formData.append("allowedMembershipIds", allowedMembershipIds);
    }
    formData.append(
      "memberEventDiscountEligible",
      String(memberEventDiscountEligible),
    );

    if (bannerSource && croppedAreaPixels) {
      try {
        const blob = await getCroppedImg(
          bannerSource,
          croppedAreaPixels,
          "blob",
        );
        const file = new File([blob], BANNER_FILENAME, { type: "image/jpeg" });
        formData.append("bannerImage", file);
      } catch (error) {
        console.error("Failed to crop banner:", error);
        return;
      }
    }

    updateEvent(formData);
  };

  const confirmDelete = () => {
    if (eventToDelete) {
      deleteEvent(eventToDelete.id);
    }
  };

  // Calculate total pages for pagination
  const totalPages = Math.ceil((events?.totalRecords ?? 0) / pageSize);

  // Handle page change for pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    refetch();
  };

  return (
    <div className="w-100">
      <AdminPanelHeader
        icon={<BsCalendarEvent size={22} />}
        title="Events"
        description="Create, edit, and manage SMBF events."
      />
      <div className="row">
        <div className="col-md-9 mb-4">
          <Form.Control
            type="text"
            placeholder={t("admin.events.searchEvents")}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-100 w-md-50"
          />
        </div>
        <div className="col-md-3">
          <Button
            className="btn-bg w-100 w-md-auto mb-3"
            onClick={handleCreate}
          >
            {t("admin.events.createEvent")}
          </Button>
        </div>
      </div>

      {/* Events Table */}
      <Table striped bordered hover responsive>
        <thead className="table-primary">
          <tr>
            <th className="align-top">{t("admin.events.code")}</th>
            <th className="align-top">
              {t("admin.events.name")}
            </th>
            <th className="align-top">{t("admin.events.description")}</th>
            <th className="align-top">{t("admin.events.city")}</th>
            <th className="align-top">{t("admin.events.currency")}</th>
            <th className="align-top">{t("admin.events.price")}</th>
            <th className="align-top">{t("admin.events.eventDate")}</th>
            <th className="align-top">{t("admin.events.earlyBird")}</th>
            <th className="align-top">{t("admin.events.earlyBirdEndDate")}</th>
            <th className="align-top">{t("admin.events.totalAvailable")}</th>
            <th className="align-top">{t("admin.events.actions")}</th>
          </tr>
        </thead>
        <tbody>
          {events?.data.map((event) => (
            <tr key={event.id}>
              <td className="align-top">{event.eventCode ?? t("common.na")}</td>
              <td className="align-top px-2 py-2">
                <Button
                  variant="link"
                  type="button"
                  onClick={() => navigate(`/admin/event/${event.id}`)}
                  className="events-table-name-link p-0 border-0 shadow-none rounded-0 text-start text-wrap lh-sm text-decoration-underline link-underline link-underline-primary link-underline-opacity-0 link-underline-opacity-100-hover"
                >
                  {event.name}
                </Button>
              </td>
              <td className="align-top">{event.description}</td>
              <td className="align-top">{event.location}</td>
              <td className="text-uppercase">
                {(event.currency || "gbp").trim()}
              </td>
              <td>
                {formatCurrencyAmount(event.price, event.currency ?? "gbp")}
              </td>
              <td>{new Date(event.eventDate).toLocaleDateString()}</td>
              <td>
                {formatCurrencyAmount(
                  event.earlyBirdPrice,
                  event.currency ?? "gbp",
                )}
              </td>
              <td>{new Date(event.earlyBirdDateEnd).toLocaleDateString()}</td>
              <td>{event.seats}</td>
              <td>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => handleEdit(event)}
                  className="me-2 text-secondary"
                >
                  <FaEdit />
                </Button>
                <Button
                  size="sm"
                  variant="link"
                  onClick={() => handleDelete(event)}
                  className="text-danger"
                >
                  <BsTrash />
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>

      {/* Pagination Controls */}
      {events?.totalRecords && events?.totalRecords > 10 && (
        <div className="d-flex justify-content-center mt-4">
          <Pagination>
            <Pagination.First
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
            />
            <Pagination.Prev
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
            />
            {[...Array(totalPages)].map((_, i) => (
              <Pagination.Item
                key={i + 1}
                active={i + 1 === currentPage}
                onClick={() => handlePageChange(i + 1)}
              >
                {i + 1}
              </Pagination.Item>
            ))}
            <Pagination.Next
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
            />
            <Pagination.Last
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
            />
          </Pagination>
        </div>
      )}

      {/* Event Create/Edit Modal */}
      <Modal
        size="lg"
        centered
        scrollable
        show={modalOpen}
        onHide={() => {
          setModalOpen(false);
          resetBannerState();
        }}
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {editingEvent ? t("admin.events.editEvent") : t("admin.events.createEvent")}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form id="event-edit-form" onSubmit={handleSave}>
            <div className="d-flex flex-column gap-3">
              <div className="row g-3 align-items-stretch">
                <div className="col-md-7 d-flex flex-column gap-3">
                  <Form.Group>
                    <Form.Label>{t("admin.events.name")}</Form.Label>
                    <Form.Control
                      name="name"
                      placeholder="e.g. An Evening with SMBF London"
                      defaultValue={editingEvent?.name || ""}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{t("admin.events.description")}</Form.Label>
                    <Form.Control
                      name="description"
                      placeholder="Brief summary for the event page and listings"
                      defaultValue={editingEvent?.description || ""}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>{t("explorer.signup.cityCountry")}</Form.Label>
                    <AsyncSelect<CityOption, false, GroupBase<CityOption>>
                      instanceId="manage-event-city"
                      inputId="manage-event-city-input"
                      aria-label={t("explorer.signup.cityCountry")}
                      loadOptions={(inputValue, callback) => {
                        loadCityOptions(inputValue, callback);
                      }}
                      placeholder={t("explorer.signup.cityPlaceholder")}
                      isClearable
                      styles={CITY_SELECT_STYLES}
                      classNamePrefix="react-select"
                      menuPortalTarget={document.body}
                      menuPosition="fixed"
                      value={
                        location
                          ? {
                              value: location,
                              label: location,
                              city: "",
                              country: "",
                            }
                          : null
                      }
                      onChange={(selected: CityOption | null) => {
                        setLocation(
                          selected
                            ? `${selected.city}, ${selected.country}`.trim()
                            : "",
                        );
                        setLocationError("");
                      }}
                    />
                    {locationError ? (
                      <Form.Text className="text-danger">{locationError}</Form.Text>
                    ) : null}
                  </Form.Group>
                </div>
                <div className="col-md-5 d-flex">
                  <Form.Group className="w-100 h-100 d-flex flex-column mb-0">
                    <Form.Label
                      className="flex-shrink-0"
                      style={{ cursor: "pointer" }}
                      onClick={openBannerFilePicker}
                    >
                      {t("admin.events.banner")}
                    </Form.Label>
                    <input
                      ref={bannerInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={onBannerFileChange}
                    />
                    {bannerSource ? (
                      <div
                        className="flex-grow-1 d-flex flex-column mb-0"
                        style={{ minHeight: 0 }}
                      >
                        <div
                          style={{ ...CROPPER_CONTAINER_STYLE, cursor: "pointer" }}
                          onPointerDown={onBannerCropPointerDown}
                          onPointerMove={onBannerCropPointerMove}
                          onPointerUp={onBannerCropPointerUp}
                          onPointerCancel={() => {
                            bannerPointerRef.current = null;
                          }}
                        >
                          <Cropper
                            image={bannerSource}
                            crop={crop}
                            zoom={zoom}
                            minZoom={BANNER_MIN_ZOOM}
                            maxZoom={BANNER_MAX_ZOOM}
                            aspect={BANNER_ASPECT}
                            restrictPosition={false}
                            onCropChange={setCrop}
                            onZoomChange={setZoom}
                            onCropComplete={onBannerCropComplete}
                          />
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="flex-grow-1 rounded border overflow-hidden bg-light mb-0 position-relative p-0 w-100 text-start"
                        style={{ minHeight: 0, cursor: "pointer" }}
                        onClick={openBannerFilePicker}
                        aria-label={t("admin.events.banner")}
                      >
                        {bannerPreview ? (
                          <img
                            src={bannerPreview}
                            alt=""
                            className="w-100 h-100 object-fit-cover position-absolute top-0 start-0"
                          />
                        ) : (
                          <div className="w-100 h-100 d-flex flex-column align-items-center justify-content-center text-muted position-absolute top-0 start-0 gap-2">
                            <BsImage size={40} aria-hidden />
                          </div>
                        )}
                      </button>
                    )}
                  </Form.Group>
                </div>
              </div>
              <div className="row g-3 align-items-end">
                <div className="col-sm-6">
                  <Form.Group>
                    <Form.Label>{t("admin.events.amount")}</Form.Label>
                    <Form.Control
                      name="price"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      defaultValue={editingEvent?.price || 0}
                      required
                    />
                  </Form.Group>
                </div>
                {/* Temporarily hidden: currency is fixed to GBP in save payload. */}
                {/* <div className="col-sm-6">
                  <Form.Group>
                    <Form.Label>Currency</Form.Label>
                    <Form.Select
                      name="currency"
                      defaultValue={
                        (editingEvent?.currency || "gbp").toLowerCase() || "gbp"
                      }
                      aria-label="Event charge currency (ISO 4217)"
                    >
                      {EVENT_CURRENCY_OPTIONS.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div> */}
                <div className="col-sm-6">
                  <Form.Group>
                    <Form.Label>Available</Form.Label>
                    <Form.Control
                      name="numberAvailable"
                      type="number"
                      placeholder="Total seats"
                      defaultValue={editingEvent?.seats || 0}
                      required
                    />
                  </Form.Group>
                </div>
              </div>
              <div>
                <Form.Group>
                  <Form.Label>{t("admin.events.productId")}</Form.Label>
                  <Form.Control
                    name="productId"
                    placeholder="Stripe Price ID (e.g. price_…)"
                    defaultValue={editingEvent?.productId || ""}
                    required
                  />
                  <Form.Text className="text-muted">
                    {t("admin.events.productIdHelp")}
                  </Form.Text>
                </Form.Group>
              </div>
              <div className="border rounded p-3">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                  <div>
                    <Form.Label className="mb-0 fw-semibold">
                      Early bird pricing
                    </Form.Label>
                    <Form.Text className="text-muted d-block small">
                      Optional discount before an end date
                    </Form.Text>
                  </div>
                  <Form.Check
                    type="switch"
                    id="early-bird-enabled"
                    label={earlyBirdEnabled ? "Enabled" : "Disabled"}
                    checked={earlyBirdEnabled}
                    onChange={(e) => {
                      const enabled = e.target.checked;
                      setEarlyBirdEnabled(enabled);
                      if (enabled && !earlyBirdEndDateInput) {
                        setEarlyBirdEndDateInput(formatYesterdayForDateInput());
                      } else if (!enabled) {
                        setEarlyBirdEndDateInput(formatYesterdayForDateInput());
                      }
                    }}
                    className="mb-0"
                  />
                </div>
                {earlyBirdEnabled ? (
                  <div className="row g-3 mt-2 pt-2 border-top">
                    <div className="col-sm-6">
                      <Form.Group>
                        <Form.Label>Early Bird Discount</Form.Label>
                        <Form.Control
                          name="earlyBirdPrice"
                          type="number"
                          step="0.01"
                          min="0"
                          placeholder="0.00"
                          defaultValue={editingEvent?.earlyBirdPrice || 0}
                          required
                        />
                      </Form.Group>
                    </div>
                    <div className="col-sm-6">
                      <Form.Group>
                        <Form.Label>Early Bird End Date</Form.Label>
                        <Form.Control
                          name="date"
                          type="date"
                          placeholder="YYYY-MM-DD"
                          title="Early bird pricing ends after this date (YYYY-MM-DD)"
                          value={earlyBirdEndDateInput}
                          onChange={(e) =>
                            setEarlyBirdEndDateInput(e.target.value)
                          }
                          required
                        />
                      </Form.Group>
                    </div>
                  </div>
                ) : null}
              </div>
              <div>
                <Form.Group>
                  <Form.Label>{t("admin.events.eventDate")}</Form.Label>
                  <Form.Control
                    name="eventDate"
                    type="date"
                    placeholder="YYYY-MM-DD"
                    title="Event date (YYYY-MM-DD)"
                    value={eventDateInput}
                    onChange={(e) => setEventDateInput(e.target.value)}
                    required
                  />
                </Form.Group>
              </div>
              <div className="border rounded p-3">
                <Form.Check
                  type="switch"
                  id="member-event-discount-eligible"
                  label="Eligible for Business / Executive member discounts"
                  checked={memberEventDiscountEligible}
                  onChange={(e) =>
                    setMemberEventDiscountEligible(e.target.checked)
                  }
                />
                <Form.Text className="text-muted d-block small mt-1">
                  Turn off for delegations and tours (full price for all members).
                  Networking events keep 50% Business and Executive
                  complimentary-first-in-month pricing.
                </Form.Text>
              </div>
              <div className="border-top pt-3 mt-1">
                <h6 className="text-secondary mb-2">Visible to membership types</h6>
                <Form.Text className="text-muted d-block mb-2">
                  Select <strong>All</strong> so every member can see and book this
                  event. Uncheck All to limit visibility to specific membership types.
                </Form.Text>
                {memberships.length === 0 ? (
                  <p className="small text-muted mb-0">Loading membership types…</p>
                ) : (
                  <div className="d-flex flex-column gap-2">
                    <Form.Check
                      type="checkbox"
                      id="event-membership-all"
                      label="All"
                      className="fw-semibold"
                      checked={visibleToAll}
                      onChange={(e) =>
                        handleVisibleToAllChange(e.target.checked)
                      }
                    />
                    {memberships.map((membership) => (
                      <Form.Check
                        key={membership.id}
                        type="checkbox"
                        id={`event-membership-${membership.id}`}
                        label={membership.name}
                        className="ms-3"
                        checked={
                          visibleToAll ||
                          selectedMembershipIds.includes(membership.id)
                        }
                        disabled={visibleToAll}
                        onChange={() => toggleMembershipSelection(membership.id)}
                      />
                    ))}
                  </div>
                )}
                {membershipSelectionError ? (
                  <div className="text-danger small mt-2">{membershipSelectionError}</div>
                ) : null}
              </div>
              <div className="border-top pt-3 mt-1">
                <h6 className="text-secondary mb-3">Booking confirmation email</h6>
                <Form.Group className="mb-3">
                  <Form.Label>Email subject</Form.Label>
                  <Form.Control
                    name="bookingConfirmationEmailSubject"
                    type="text"
                    placeholder="Booking Confirmed: {EventName}"
                    defaultValue={editingEvent?.bookingConfirmationEmailSubject ?? ""}
                  />
                </Form.Group>
                <Form.Group>
                  <Form.Label>Email body text</Form.Label>
                  <Form.Control
                    as="textarea"
                    name="bookingConfirmationEmailBodyHtml"
                    rows={8}
                    placeholder={
                      "Optional — leave subject and body empty to send no confirmation email.\n\n" +
                      "Example: We look forward to seeing you at {EventName} on {EventDate} at {EventLocation}. Your reference code is {EventCode}."
                    }
                    defaultValue={editingEvent?.bookingConfirmationEmailBodyHtml ?? ""}
                  />
                </Form.Group>
              </div>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setModalOpen(false);
              resetBannerState();
            }}
          >
            {t("connections.cancel")}
          </Button>
          <Button
            type="submit"
            form="event-edit-form"
            variant="primary"
            disabled={isPending}
          >
            {isPending ? t("admin.events.saving") : t("admin.events.save")}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        centered
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("admin.events.confirmDeletion")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t("admin.events.confirmDeletePrompt")} <strong>{eventToDelete?.name}</strong>
          ?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            {t("connections.cancel")}
          </Button>
          <Button
            disabled={isDeleting}
            variant="danger"
            onClick={confirmDelete}
          >
            {isDeleting ? t("admin.events.deleting") : t("feed.delete")}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default ManageEvents;
